<template>
    <!--
        Ficha somente leitura de uma pessoa da inscrição (aluno, pai, mãe ou responsáveis),
        agrupada por assunto. Usada pelas abas de DadosCaptacao.vue.
    -->
    <div class="pc-ficha">
        <section class="pc-secao">
            <h6 class="pc-secao__titulo"><b-icon icon="person-badge"></b-icon> Identificação</h6>
            <div class="pc-campos">
                <div class="pc-campo pc-campo--largo">
                    <span class="pc-campo__rotulo">Nome</span>
                    <span class="pc-campo__valor pc-campo__valor--destaque">{{ valor(dadosGerais.Nome) }}</span>
                </div>
                <div class="pc-campo pc-campo--largo">
                    <span class="pc-campo__rotulo">Nome social</span>
                    <span class="pc-campo__valor">{{ valor(dadosGerais.NomeSocial) }}</span>
                </div>
                <div class="pc-campo">
                    <span class="pc-campo__rotulo">CPF</span>
                    <span class="pc-campo__valor">{{ valor(formatarCpf(documentos.CPF)) }}</span>
                </div>
                <div class="pc-campo">
                    <span class="pc-campo__rotulo">Data de nascimento</span>
                    <span class="pc-campo__valor">{{ valor(formatarData(dadosGerais.DataNascimento)) }}</span>
                </div>
                <div class="pc-campo">
                    <span class="pc-campo__rotulo">Gênero</span>
                    <span class="pc-campo__valor">{{ valor(dadosGerais.Sexo) }}</span>
                </div>
                <div class="pc-campo" v-if="complemento">
                    <span class="pc-campo__rotulo">Estado civil</span>
                    <span class="pc-campo__valor">{{ valor(complemento.estadoCivilNome) }}</span>
                </div>
                <div class="pc-campo" v-if="complemento">
                    <span class="pc-campo__rotulo">Etnia</span>
                    <span class="pc-campo__valor">{{ valor(complemento.etnia_new) }}</span>
                </div>
                <div class="pc-campo" v-if="complemento">
                    <span class="pc-campo__rotulo">ID CRM</span>
                    <span class="pc-campo__valor">{{ valor(complemento.id) }}</span>
                </div>
            </div>
        </section>

        <section class="pc-secao">
            <h6 class="pc-secao__titulo"><b-icon icon="card-text"></b-icon> Documento de identidade</h6>
            <div class="pc-campos">
                <div class="pc-campo">
                    <span class="pc-campo__rotulo">RG</span>
                    <span class="pc-campo__valor">{{ valor(rg.Numero) }}</span>
                </div>
                <div class="pc-campo">
                    <span class="pc-campo__rotulo">Órgão expedidor</span>
                    <span class="pc-campo__valor">{{ valor(rg.OrgaoEmissor) }}</span>
                </div>
                <div class="pc-campo">
                    <span class="pc-campo__rotulo">Data de expedição</span>
                    <span class="pc-campo__valor">{{ valor(rg.DataExpedicao) }}</span>
                </div>
                <div class="pc-campo">
                    <span class="pc-campo__rotulo">UF do RG</span>
                    <span class="pc-campo__valor">{{ valor(rg.UFExpedicao) }}</span>
                </div>
            </div>
        </section>

        <section class="pc-secao">
            <h6 class="pc-secao__titulo"><b-icon icon="globe"></b-icon> Naturalidade</h6>
            <div class="pc-campos">
                <div class="pc-campo">
                    <span class="pc-campo__rotulo">Cidade</span>
                    <span class="pc-campo__valor">{{ valor(dadosGerais.CidadeNascimento) }}</span>
                </div>
                <div class="pc-campo">
                    <span class="pc-campo__rotulo">Estado</span>
                    <span class="pc-campo__valor">{{ valor(dadosGerais.UFNascimento) }}</span>
                </div>
                <div class="pc-campo">
                    <span class="pc-campo__rotulo">País</span>
                    <span class="pc-campo__valor">{{ valor(dadosGerais.PaisNascimento) }}</span>
                </div>
            </div>
        </section>

        <section class="pc-secao">
            <h6 class="pc-secao__titulo"><b-icon icon="telephone"></b-icon> Contato</h6>
            <div class="pc-campos">
                <div class="pc-campo" v-for="(telefone, i) in telefones" :key="'tel'+i">
                    <span class="pc-campo__rotulo">Telefone{{ telefones.length>1 ? ' '+(i+1) : '' }}</span>
                    <span class="pc-campo__valor">
                        <a :href="'tel:'+somenteDigitos(telefone)">{{ formatarTelefone(telefone) }}</a>
                    </span>
                </div>
                <div class="pc-campo" v-if="telefones.length==0">
                    <span class="pc-campo__rotulo">Telefone</span>
                    <span class="pc-campo__valor">—</span>
                </div>
                <div class="pc-campo pc-campo--largo" v-for="(email, i) in emails" :key="'mail'+i">
                    <span class="pc-campo__rotulo">E-mail{{ emails.length>1 ? ' '+(i+1) : '' }}</span>
                    <span class="pc-campo__valor"><a :href="'mailto:'+email">{{ email }}</a></span>
                </div>
                <div class="pc-campo pc-campo--largo" v-if="emails.length==0">
                    <span class="pc-campo__rotulo">E-mail</span>
                    <span class="pc-campo__valor">—</span>
                </div>
            </div>
        </section>

        <section class="pc-secao">
            <h6 class="pc-secao__titulo"><b-icon icon="house-door"></b-icon> Endereço</h6>
            <div class="pc-campos">
                <div class="pc-campo">
                    <span class="pc-campo__rotulo">CEP</span>
                    <span class="pc-campo__valor">{{ valor(formatarCep(endereco.Cep)) }}</span>
                </div>
                <div class="pc-campo pc-campo--largo">
                    <span class="pc-campo__rotulo">Logradouro</span>
                    <span class="pc-campo__valor">{{ valor(logradouro) }}</span>
                </div>
                <div class="pc-campo">
                    <span class="pc-campo__rotulo">Bairro</span>
                    <span class="pc-campo__valor">{{ valor(endereco.Bairro) }}</span>
                </div>
                <div class="pc-campo">
                    <span class="pc-campo__rotulo">Cidade</span>
                    <span class="pc-campo__valor">{{ valor(endereco.Cidade) }}</span>
                </div>
                <div class="pc-campo">
                    <span class="pc-campo__rotulo">UF</span>
                    <span class="pc-campo__valor">{{ valor(endereco.UF) }}</span>
                </div>
                <div class="pc-campo">
                    <span class="pc-campo__rotulo">País</span>
                    <span class="pc-campo__valor">{{ valor(endereco.Pais) }}</span>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
export default {
    props:{
        // objeto da inscrição (InformacoesWebHook.Pessoa, .Pai, .Mae, .RespEducacional ou .RespFinanceiro)
        pessoa:{ type:Object, required:true },
        // dados complementares do CRM (prospect.inscrito, .pai, .mae...): id, estado civil e etnia
        complemento:{ type:Object, default:null },
    },
    computed:{
        dadosGerais(){
            return this.pessoa.DadosGerais || {}
        },
        documentos(){
            return this.pessoa.Documentos || {}
        },
        rg(){
            return this.documentos.RG || {}
        },
        endereco(){
            return (this.pessoa.Enderecos && this.pessoa.Enderecos.length>0 ? this.pessoa.Enderecos[0] : {})
        },
        logradouro(){
            if(!this.endereco.Logradouro){
                return null
            }
            return (this.endereco.Numero ? this.endereco.Logradouro+', '+this.endereco.Numero : this.endereco.Logradouro)
        },
        contatos(){
            return (this.pessoa.Contatos || []).filter(contato => contato.TipoContato!=null && contato.Contato)
        },
        telefones(){
            return this.contatos.filter(contato => !this.isMail(contato.Contato)).map(contato => contato.Contato)
        },
        emails(){
            return this.contatos.filter(contato => this.isMail(contato.Contato)).map(contato => contato.Contato)
        },
    },
    methods:{
        valor(texto){
            return (texto===null || texto===undefined || (''+texto).trim()=='' ? '—' : texto)
        },
        isMail(texto){
            return (''+texto).indexOf('@')>=0
        },
        somenteDigitos(texto){
            return (''+(texto || '')).replace(/\D/g,'')
        },
        formatarCpf(cpf){
            let digitos = this.somenteDigitos(cpf)
            return (digitos.length==11 ? digitos.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/,'$1.$2.$3-$4') : cpf)
        },
        formatarCep(cep){
            let digitos = this.somenteDigitos(cep)
            return (digitos.length==8 ? digitos.replace(/(\d{5})(\d{3})/,'$1-$2') : cep)
        },
        formatarTelefone(telefone){
            let digitos = this.somenteDigitos(telefone)
            if(digitos.length==11){
                return digitos.replace(/(\d{2})(\d{5})(\d{4})/,'($1) $2-$3')
            }else if(digitos.length==10){
                return digitos.replace(/(\d{2})(\d{4})(\d{4})/,'($1) $2-$3')
            }
            return telefone
        },
        // mesma conversão usada antes no campo (formatDate(...,2) do DadosCaptacao)
        formatarData(data){
            if(!data){
                return null
            }
            let d = new Date(data)
            if(isNaN(d.getTime())){
                return data
            }
            let dia = ('0'+d.getDate()).slice(-2)
            let mes = ('0'+(d.getMonth()+1)).slice(-2)
            let dt = [dia, mes, d.getFullYear()].join('/')
            return (dt=='31/12/1969' ? null : dt)
        },
    },
}
</script>

<style lang="scss" scoped>
@import '../../../sass/variables';

.pc-ficha {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
    padding: 1.25rem 0 .5rem;
}

.pc-secao {
    border: 1px solid $ec-borda;
    border-radius: $border-radius-lg;
    padding: .9rem 1rem 1rem;
    background: $ec-superficie;

    // identificação ocupa a linha inteira
    &:first-child { grid-column: 1 / -1; }
}

.pc-secao__titulo {
    display: flex;
    align-items: center;
    gap: .45rem;
    margin: 0 0 .85rem;
    font-size: .72rem;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: $ec-petroleo;
}

.pc-campos {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: .85rem 1.25rem;
}

.pc-campo {
    display: flex;
    flex-direction: column;
    min-width: 0;

    &--largo { grid-column: span 2; }
}

.pc-campo__rotulo {
    font-size: .72rem;
    font-weight: 600;
    color: $ec-texto-suave;
    margin-bottom: .15rem;
}

.pc-campo__valor {
    font-size: .9rem;
    color: $ec-texto;
    overflow-wrap: anywhere;

    &--destaque { font-weight: 700; }
}

@media (max-width: 991.98px) {
    .pc-ficha { grid-template-columns: minmax(0, 1fr); }
}

@media (max-width: 575.98px) {
    .pc-campo--largo { grid-column: 1 / -1; }
}
</style>
